<script lang="ts" setup>
// One icon set for the dashboard's own icons: Lucide, drawn a little over the text size (1.2em; Lucide's thin line reads small at 1em) in the current colour.
// It takes the names the app always used (Font Awesome's), as a string or ['fas', name] /
// ['fab', name], so menus that pass icon names around as data keep working unchanged.
// PrimeIcons (pi pi-*) are a separate set and are not touched by this.
import type { Component } from 'vue';
// Named imports, so only these end up in the build
import {
  AlignLeft, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ArrowUpDown, Ban, Banknote, BatteryFull, BatteryLow, BatteryMedium, BatteryWarning, Bell, Bold, BookUser, Building, Building2, Calculator, Calendar, Calendar1, CalendarDays, CalendarPlus, ChartLine, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Circle, CircleAlert, CircleCheck, CircleDot, CircleHelp, CircleStop, CircleX, ClipboardList, Clock, Code, Copy, CopyPlus, CreditCard, Ellipsis, Equal, Euro, ExternalLink, Eye, EyeOff, File as FileIcon, FileInput, FileText, FileUp, Funnel, Gift, GitBranch, Grip, GripVertical, Hand, Heading, Heart, History as HistoryIcon, House, Image as ImageIcon, Inbox, Info, Italic, KeyRound, Languages, LayoutDashboard, Link, List, ListChecks, ListOrdered, LockOpen, Mail, Map as MapIcon, MapPin, Menu, MessageSquare, Minus, Monitor, MousePointer2, PaintBucket, Paintbrush, Palette, Paperclip, Pen, Percent, Phone, Plus, QrCode, Receipt, Redo2, RefreshCw, Reply, RotateCcw, Save, ScanBarcode, Search, Send, Settings, ShieldCheck, ShieldUser, Signal, Smartphone, Square, SquareCheck, SquareChevronDown, SquareDashed, SquarePen, Stamp, Star, Store, Strikethrough, Sun, SwatchBook, Table, Tag, Tags, Ticket, Trash2, TriangleAlert, Type, Underline, Undo2, Upload, User, UserPlus, Users, UsersRound, Workflow, Wrench, X, Zap,
} from 'lucide-vue-next';

const props = defineProps<{ icon: string | readonly string[] }>();

// Font Awesome name (and the aliases the app uses) -> Lucide component
const MAP: Record<string, Component> = {
  'add': Plus, 'plus': Plus, 'minus': Minus, 'remove': X, 'xmark': X, 'close': X, 'cancel': Ban, 'ban': Ban,
  'check': Check, 'circle-check': CircleCheck, 'circle-xmark': CircleX, 'circle-exclamation': CircleAlert,
  'circle-info': Info, 'info': Info, 'question': CircleHelp, 'circle-stop': CircleStop, 'circle': Circle,
  'triangle-exclamation': TriangleAlert, 'warning': TriangleAlert,
  'address-book': BookUser, 'align-left': AlignLeft, 'arrow-pointer': MousePointer2,
  'angles-left': ChevronsLeft, 'angles-right': ChevronsRight,
  'arrow-down': ArrowDown, 'arrow-up': ArrowUp, 'arrow-left': ArrowLeft, 'arrow-right': ArrowRight,
  'arrows-up-down': ArrowUpDown, 'arrow-up-right-from-square': ExternalLink,
  'chevron-down': ChevronDown, 'chevron-left': ChevronLeft, 'chevron-right': ChevronRight,
  'barcode': ScanBarcode, 'qrcode': QrCode, 'bars': Menu, 'ellipsis': Ellipsis,
  'battery-empty': BatteryWarning, 'battery-quarter': BatteryLow, 'battery-half': BatteryMedium,
  'battery-three-quarters': BatteryMedium, 'battery-full': BatteryFull,
  'bell': Bell, 'bolt': Zap, 'bold': Bold, 'italic': Italic, 'underline': Underline, 'strikethrough': Strikethrough,
  'heading': Heading, 'header': Heading, 'font': Type, 'list': List, 'list-ol': ListOrdered, 'list-ul': List,
  'list-check': ListChecks, 'link': Link, 'paperclip': Paperclip, 'image': ImageIcon, 'table': Table,
  'border-none': SquareDashed, 'square': Square, 'square-check': SquareCheck, 'caret-square-down': SquareChevronDown,
  'building': Building2, 'city': Building, 'store': Store, 'shop': Store, 'cash-register': Calculator,
  'calendar': Calendar, 'calendar-day': Calendar1, 'calendar-days': CalendarDays, 'calendar-plus': CalendarPlus,
  'clock': Clock, 'history': HistoryIcon, 'timeline': HistoryIcon, 'sun': Sun,
  'chart-line': ChartLine, 'dashboard': LayoutDashboard, 'display': Monitor, 'mobile': Smartphone, 'signal': Signal,
  'clipboard-list': ClipboardList, 'clipboard-question': ClipboardList, 'clone': CopyPlus, 'copy': Copy,
  'code': Code, 'code-branch': GitBranch, 'diagram-project': Workflow,
  'gear': Settings, 'cog': Settings, 'screwdriver-wrench': Wrench, 'key': KeyRound, 'unlock': LockOpen,
  'user': User, 'user-plus': UserPlus, 'user-shield': ShieldUser, 'users': Users, 'users-line': UsersRound,
  'envelope': Mail, 'inbox': Inbox, 'paper-plane': Send, 'reply': Reply, 'message': MessageSquare,
  'phone': Phone, 'language': Languages, 'map': MapIcon, 'location': MapPin, 'location-dot': MapPin,
  'euro': Euro, 'euro-sign': Euro, 'money-bill': Banknote, 'credit-card': CreditCard, 'receipt': Receipt,
  'percentage': Percent, 'tag': Tag, 'tags': Tags, 'gift': Gift, 'ticket': Ticket, 'ticket-simple': Ticket,
  'stamp': Stamp, 'star': Star, 'heart': Heart, 'shield-heart': ShieldCheck, 'radio': CircleDot,
  'eye': Eye, 'eye-slash': EyeOff, 'equals': Equal, 'filter': Funnel, 'search': Search, 'sort': ArrowUpDown,
  'file': FileIcon, 'file-arrow-up': FileUp, 'file-import': FileInput, 'file-pdf': FileText, 'upload': Upload,
  'save': Save, 'trash': Trash2, 'edit': SquarePen, 'pen': Pen, 'paintbrush': Paintbrush, 'palette': Palette,
  'swatchbook': SwatchBook, 'fill': PaintBucket, 'grip': Grip, 'grip-vertical': GripVertical, 'hand': Hand,
  'home': House, 'refresh': RefreshCw, 'rotate': RefreshCw, 'rotate-left': RotateCcw, 'undo': Undo2, 'redo': Redo2,
};

const parts = computed(() => (typeof props.icon === 'string' ? ['fas', props.icon] : props.icon) as string[]);
const brand = computed(() => (parts.value[0] === 'fab' ? brandIcons[parts.value[1] ?? ''] : undefined));
const component = computed(() => {
  const name = MAP[parts.value[1] ?? ''];
  if (!name && !brand.value && import.meta.dev) console.warn(`AppIcon: no Lucide icon for '${parts.value[1]}'`);
  return name ?? null;
});
</script>

<template>
  <svg v-if="brand" class="app-icon" :viewBox="brand.viewBox" fill="currentColor" aria-hidden="true">
    <path :d="brand.d" />
  </svg>
  <component :is="component" v-else-if="component" class="app-icon" aria-hidden="true" />
</template>

<style>
/* Sized by the text around it, like the icon font it replaces; class="fa-fw" for a fixed width */
.app-icon {
  display: inline-block;
  width: 1.2em;
  height: 1.2em;
  stroke-width: 2;
  vertical-align: -0.225em;
  flex-shrink: 0;
}
.app-icon.fa-fw {
  width: 1.45em;
}
</style>
